import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import PageHeader from '../../PageHeader/PageHeader'
import AppTable from '../../components/AppTable/AppTable'
import AppPagination from '../../components/Pagination/AppPagination'
import { HitApi } from '../../Api/ApiHit'
import { searchServiceFollowup, deleteServiceFollowup, searchUser } from '../../constant/Constant'
import { useDispatch, useSelector } from 'react-redux'
import { setApiJson } from '../../redux/Actions/ApiAction'
import { setPagination } from '../../redux/Actions/TableAction'
import { getSelectedUnit, getElevateUser } from '../../storage/Storage'
import toast from 'react-hot-toast'
import DeleteButton from '../../components/Delete/DeleteButton'
import EditButton from '../../components/Edit/EditButton'
import TableSearch from '../../components/TableSearch/TableSearch'
import DateSearch from '../../components/DateSearch/DateSearch'
import { Search, X } from 'lucide-react'

function ClientFollowup() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const ApiReducer = useSelector((state) => state.ApiReducer)
    const TableDataReducer = useSelector((state) => state.TableReducer)

    const currentUser = getElevateUser()
    const isSuperAdmin = currentUser?.roleData?.roleType === 'SuperAdmin' && currentUser?.userType === 'SuperAdmin'

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [optionSelect, setOptionSelect] = useState('')
    const [valueSearched, setValueSearched] = useState('')
    const [isSearching, setIsSearching] = useState(false)

    // Date range filter states
    const [activeDateRange, setActiveDateRange] = useState(null)
    const [searchMode, setSearchMode] = useState('single')
    const [singleDate, setSingleDate] = useState(new Date().toISOString().split('T')[0])

    // Super admin user search states
    const [selectedUser, setSelectedUser] = useState(null)
    const [userSearchTerm, setUserSearchTerm] = useState('')
    const [userResults, setUserResults] = useState([])
    const [showUserDropdown, setShowUserDropdown] = useState(false)
    const [isLoadingUsers, setIsLoadingUsers] = useState(false)
    const userDropdownRef = useRef(null)

    // Initialize with empty array
    useEffect(() => {
        if (!Array.isArray(ApiReducer.apiJson)) {
            dispatch(setApiJson([]))
        }
    }, [])

    // Fetch follow-ups
    const getFollowups = useCallback(async (searchKey, searchValue) => {
        // Super admin must select a user first
        if (isSuperAdmin && !selectedUser) {
            dispatch(setApiJson([]))
            return
        }

        setIsLoading(true)
        setIsSearching(true)
        setError(null)

        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id
            }

            // Super admin: filter by selected user's followupBy
            if (isSuperAdmin && selectedUser) {
                searchObj['followupBy'] = selectedUser._id
            } else if (!isSuperAdmin) {
                // Non super admin: only see their own follow-ups
                searchObj['followupBy'] = currentUser?._id
            }

            // Add search filters if provided
            if (searchKey && searchValue) {
                if (searchKey === 'clientName') {
                    searchObj['clientId.name'] = {
                        $regex: searchValue,
                        $options: 'i'
                    }
                } else if (searchKey === 'status') {
                    searchObj[searchKey] = searchValue.toLowerCase()
                } else {
                    searchObj[searchKey] = {
                        $regex: searchValue,
                        $options: 'i'
                    }
                }
            }

            // Apply date filter
            if (searchMode === 'single' && singleDate) {
                const startOfDay = new Date(singleDate)
                startOfDay.setHours(0, 0, 0, 0)
                const endOfDay = new Date(singleDate)
                endOfDay.setHours(23, 59, 59, 999)
                searchObj['serviceFollowup.followupDate'] = {
                    $gte: startOfDay.getTime(),
                    $lte: endOfDay.getTime()
                }
            } else if (searchMode === 'range' && activeDateRange) {
                const startOfDay = new Date(activeDateRange.startDate)
                startOfDay.setHours(0, 0, 0, 0)
                const endOfDay = new Date(activeDateRange.endDate)
                endOfDay.setHours(23, 59, 59, 999)
                searchObj['serviceFollowup.followupDate'] = {
                    $gte: startOfDay.getTime(),
                    $lte: endOfDay.getTime()
                }
            }

            const json = {
                page: TableDataReducer.pagination.page,
                limit: TableDataReducer.pagination.limit,
                search: searchObj
            }

            const res = await HitApi(json, searchServiceFollowup)

            if (res?.statusCode === 200) {
                const followupsData = res?.data?.docs || []
                const totalCount = res?.data?.totalDocs || 0

                dispatch(setApiJson(followupsData))

                const paginationData = {
                    total: totalCount,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                }

                dispatch(setPagination(paginationData))
            } else {
                const errorMessage = res?.message || 'Failed to fetch follow-ups'
                console.error("API Error:", errorMessage)
                setError(errorMessage)
                dispatch(setApiJson([]))
                toast.error('Failed to fetch follow-ups')
            }
        } catch (error) {
            console.error("Error fetching follow-ups:", error)
            setError('Failed to fetch follow-ups. Please try again.')
            dispatch(setApiJson([]))
            toast.error('Error fetching follow-ups')
        } finally {
            setIsLoading(false)
            setIsSearching(false)
        }
    }, [dispatch, TableDataReducer.pagination.page, TableDataReducer.pagination.limit, selectedUser, isSuperAdmin, searchMode, singleDate, activeDateRange])

    useEffect(() => {
        getFollowups()
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, selectedUser, singleDate, activeDateRange, searchMode])

    // Search functionality
    const handleSearch = () => {
        if (!optionSelect) {
            toast.error('Please select a search option')
            return
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value')
            return
        }

        getFollowups(optionSelect, valueSearched.trim())
    }

    const handleClearSearch = () => {
        setOptionSelect('')
        setValueSearched('')
        getFollowups()
    }

    // Date range handlers
    const handleDateRangeSearch = (startDate, endDate) => {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const daysDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24))
        if (daysDiff > 90) {
            toast.error('Date range cannot exceed 90 days')
            return
        }
        setActiveDateRange({ startDate, endDate })
    }

    const handleClearDateRange = () => {
        setActiveDateRange(null)
        setSingleDate(new Date().toISOString().split('T')[0])
    }

    const handleSingleDateSearch = () => {
        if (!singleDate) {
            toast.error('Please select a date')
            return
        }
        getFollowups()
    }

    // Super admin: user search
    const fetchUsers = async (query) => {
        setIsLoadingUsers(true)
        try {
            const json = {
                page: 1,
                limit: 20,
                search: {
                    name: { $regex: query, $options: 'i' }
                }
            }
            const res = await HitApi(json, searchUser)
            if (res?.statusCode === 200) {
                setUserResults(Array.isArray(res?.data?.docs) ? res.data.docs : [])
            } else {
                setUserResults([])
            }
        } catch (error) {
            setUserResults([])
        } finally {
            setIsLoadingUsers(false)
        }
    }

    useEffect(() => {
        if (showUserDropdown && userSearchTerm.trim()) {
            fetchUsers(userSearchTerm)
        } else {
            setUserResults([])
        }
    }, [showUserDropdown, userSearchTerm])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
                setShowUserDropdown(false)
            }
        }
        if (showUserDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showUserDropdown])

    const handleUserSelect = (user) => {
        setSelectedUser(user)
        setShowUserDropdown(false)
        setUserSearchTerm('')
        setUserResults([])
    }

    const handleClearUser = () => {
        setSelectedUser(null)
        setUserSearchTerm('')
        setUserResults([])
    }

    // Search options
    const options = [
        { label: 'Client Name', value: 'clientName' },
        { label: 'Status', value: 'status' },
        { label: 'Follow-up By', value: 'followupBy.name' }
    ]

    // Handle actions
    const handleEdit = useCallback((row) => {
        if (row?._id) {
            navigate(`/clientFollowup/edit/${row._id}`)
        } else {
            toast.error('Unable to edit: ID not found')
        }
    }, [navigate])

    const handleDelete = useCallback(async (row) => {
        const followupId = row?._id
        if (!followupId) {
            toast.error('Unable to delete: ID not found')
            return
        }

        const clientName = row?.clientId?.name || 'this follow-up'
        const confirmMessage = `Are you sure you want to delete follow-up for "${clientName}"?`

        if (window.confirm(confirmMessage)) {
            setIsLoading(true)
            try {
                const json = { _id: followupId }
                const res = await HitApi(json, deleteServiceFollowup)

                if (res?.statusCode === 200) {
                    toast.success('Follow-up deleted successfully!')
                    await getFollowups()
                } else {
                    throw new Error(res?.message || 'Failed to delete follow-up')
                }
            } catch (error) {
                console.error("Error deleting follow-up:", error)
                toast.error(`Failed to delete: ${error.message}`)
            } finally {
                setIsLoading(false)
            }
        }
    }, [getFollowups])

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A'
        const date = new Date(timestamp)
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        })
    }

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit

    // Table headers
    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "start",
            priority: 1,
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                )
            }
        },
        {
            title: "Client",
            key: "clientId",
            priority: 1,
            render: (value) => (
                <div>
                    <div className="font-medium text-gray-900">
                        {value?.name || 'N/A'}
                    </div>
                    {value?.phoneNumber && (
                        <div className="text-xs text-gray-500">
                            📱 {value.phoneNumber}
                        </div>
                    )}
                    {value?.customerType && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                            {value.customerType}
                        </span>
                    )}
                </div>
            )
        },
        {
            title: "Follow-up By",
            key: "followupBy",
            priority: 2,
            render: (value) => (
                <div>
                    <div className="text-sm font-medium text-gray-700">
                        {value?.name || 'N/A'}
                    </div>
                    {value?.email && (
                        <div className="text-xs text-gray-500">{value.email}</div>
                    )}
                </div>
            )
        },
        {
            title: "Unit",
            key: "unitId",
            priority: 3,
            render: (value) => (
                <div>
                    <div className="text-sm font-medium text-gray-700">
                        {value?.unitName || 'N/A'}
                    </div>
                    {value?.unitCode && (
                        <div className="text-xs text-gray-500">{value.unitCode}</div>
                    )}
                </div>
            )
        },
        {
            title: "Status",
            key: "status",
            align: "start",
            priority: 2,
            render: (value) => (
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    value === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'N/A'}
                </span>
            )
        },
        {
            title: "Services",
            key: "serviceFollowup",
            priority: 3,
            render: (value) => {
                if (!value || !Array.isArray(value) || value.length === 0) {
                    return <span className="text-sm text-gray-500">No services</span>
                }

                return (
                    <div className="space-y-3">
                        {value.map((service, idx) => (
                            <div key={idx} className="flex items-start gap-2 pb-2 border-b last:border-0">
                                {/* Service Image */}
                                {service.serviceId?.img ? (
                                    <img
                                        src={service.serviceId.img}
                                        alt={service.serviceId.name}
                                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                                        onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-sm">✂️</span>
                                    </div>
                                )}

                                {/* Service Details */}
                                <div className="flex-1 min-w-0">
                                    {/* Service Name */}
                                    <div className="font-medium text-gray-900 text-sm truncate">
                                        {service.serviceId?.name || (
                                            <span className="text-gray-400 italic">No service</span>
                                        )}
                                    </div>

                                    {/* Price Details */}
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-gray-600">
                                            ₹{service.originalPrice}
                                        </span>
                                        <span className="text-xs text-gray-400">→</span>
                                        <span className="text-xs font-semibold text-green-600">
                                            ₹{service.negotiated}
                                        </span>
                                        {parseFloat(service.discount) !== 0 && (
                                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                -{Math.abs(parseFloat(service.discount))}
                                            </span>
                                        )}
                                    </div>


                                    {/* Communication (Truncated) */}
                                    {service.communication && (
                                        <div
                                            className="text-xs text-gray-600 mt-1 line-clamp-2"
                                            title={service.communication}
                                        >
                                            💬 {service.communication}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            }
        },
        {
            title: "Appointment",
            key: "serviceFollowup",
            priority: 4,
            render: (value) => {
                const date = value?.[0]?.followupDate
                if (!date) return <span className="text-sm text-gray-400">N/A</span>
                return (
                    <div className="text-xs text-gray-600 space-y-0.5">
                        <div>📅 {formatDate(date)}</div>
                        <div>🕐 {new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                    </div>
                )
            }
        },
        {
            title: "Actions",
            key: "actions",
            align: "start",
            priority: 1,
            render: (value, row) => (
                <div className="flex justify-start space-x-2">
                    <EditButton
                        onEdit={() => handleEdit(row)}
                        itemName="follow-up"
                        disabled={isLoading}
                    />
                    <DeleteButton
                        onDelete={() => handleDelete(row)}
                        itemName="follow-up"
                        disabled={isLoading}
                    />
                </div>
            )
        }
    ]

    const followupsData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []

    return (
        <div className='p-5'>
            <PageHeader
                title="Client Follow-Ups"
                description="Manage client follow-ups and communications"
            />

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-medium">Error Loading Follow-ups</h4>
                            <p className="mt-1">{error}</p>
                        </div>
                        <button
                            onClick={() => getFollowups()}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {isLoading ? 'Loading...' : 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            {isSuperAdmin && (
                <div className="mb-4 bg-white p-3 border rounded-lg">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by User</label>
                    <div className="relative" ref={userDropdownRef}>
                        <input
                            type="text"
                            placeholder="Search user by name..."
                            value={selectedUser ? selectedUser.name : userSearchTerm}
                            onChange={(e) => { if (!selectedUser) setUserSearchTerm(e.target.value) }}
                            onFocus={() => !selectedUser && setShowUserDropdown(true)}
                            className="w-full px-3 py-2 pr-16 text-sm border rounded focus:ring-1 focus:ring-blue-500"
                        />
                        <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                        {selectedUser && (
                            <button onClick={handleClearUser} className="absolute right-8 top-2.5 text-red-500 hover:text-red-700">
                                <X className="h-4 w-4" />
                            </button>
                        )}
                        {showUserDropdown && !selectedUser && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                                {isLoadingUsers ? (
                                    <div className="p-3 text-center">
                                        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                                    </div>
                                ) : userResults.length > 0 ? (
                                    userResults.map((user) => (
                                        <button
                                            key={user._id}
                                            onClick={() => handleUserSelect(user)}
                                            className="w-full p-2.5 text-left hover:bg-blue-50 border-b last:border-0"
                                        >
                                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                            {user.email && <div className="text-xs text-gray-500">{user.email}</div>}
                                        </button>
                                    ))
                                ) : (
                                    <div className="p-3 text-xs text-center text-gray-500">
                                        {userSearchTerm ? 'No users found' : 'Type to search'}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {selectedUser && (
                        <p className="mt-1.5 text-xs text-gray-500">
                            Showing follow-ups by: <span className="font-medium text-blue-700">{selectedUser.name}</span>
                        </p>
                    )}
                </div>
            )}

            {/* Date Range Filter */}
            <div className="mb-4 bg-white p-3 border rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                    <button
                        onClick={() => { setSearchMode('single'); setActiveDateRange(null) }}
                        className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${searchMode === 'single' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        Single Date
                    </button>
                    <button
                        onClick={() => { setSearchMode('range') }}
                        className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${searchMode === 'range' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        Date Range
                    </button>
                </div>

                {searchMode === 'single' ? (
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Date:</label>
                            <input
                                type="date"
                                value={singleDate}
                                onChange={(e) => setSingleDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                                className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                        </div>
                    </div>
                ) : (
                    <DateSearch
                        onSearch={handleDateRangeSearch}
                        onClear={handleClearDateRange}
                    />
                )}
            </div>

            {isSuperAdmin && !selectedUser ? (
                <div className="bg-white border rounded-lg p-16 text-center">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Please select a staff to see follow-ups</p>
                    <p className="text-xs text-gray-400 mt-1">Use the search above to find a staff member</p>
                </div>
            ) : (
                <AppTable
                    TH={tableHeaders}
                    TD={followupsData}
                    isLoading={isLoading || isSearching}
                    emptyMessage={error ? "Failed to load follow-ups" : "No follow-ups found. Click 'Add Follow-up' to get started."}
                    buttonText="Add Follow-up"
                    navigateTo="/clientFollowup/add"
                    TableSearch={
                        <TableSearch
                            options={options}
                            onClick={handleSearch}
                            optionSelect={optionSelect}
                            setOptionSelect={setOptionSelect}
                            setValueSearched={setValueSearched}
                            valueSearched={valueSearched}
                            onClear={handleClearSearch}
                        />
                    }
                />
            )}
        </div>
    )
}

export default ClientFollowup